import type { WebContainer } from '@webcontainer/api';
import { persistUserFile } from '../runtime/tool-adapters/writeFile';
import { workbenchStore } from '../stores/workbench';
import { getHeadlessTemplateFiles } from '../templates/headlessTemplate';
import { materializeHotEditBridgeFilesInStore } from './mergedFiles';

const IDEoz_BRIDGE_PREFIX = 'src/ideoz/';

/**
 * Ensures snapshot restores (and legacy sandboxes) include the iframe preview bridge.
 * Idempotent; safe to call multiple times.
 */
export function patchMainTsxForBridge(content: string): string {
  if (content.includes('initPreviewBridge')) return content;

  const injectLines =
    '\nimport { initPreviewBridge } from "./ideoz/previewBridge";\ninitPreviewBridge();\n';

  const cssImport = /import\s+["']\.?\/?index\.css["'];?\r?\n/;
  if (cssImport.test(content)) return content.replace(cssImport, (m) => `${m}${injectLines}`);

  return `${injectLines}${content}`;
}

const RENDER_SIGNAL_SCRIPT_TAG = '<script type="module" src="/src/ideoz/renderSignal.ts"></script>';

/**
 * Loads `src/ideoz/renderSignal.ts` from `index.html` ahead of `src/main.tsx`, so the preview
 * still reports a compile error when `main.tsx` never runs. Idempotent.
 */
export function patchIndexHtmlForRenderSignal(content: string): string {
  if (content.includes('src/ideoz/renderSignal')) return content;

  const mainScript = /<script\b[^>]*\bsrc=["'][^"']*main\.tsx["'][^>]*>\s*<\/script>/i;
  const match = mainScript.exec(content);
  if (match) {
    const lineStart = content.lastIndexOf('\n', match.index) + 1;
    const indent = /^[ \t]*/.exec(content.slice(lineStart, match.index))?.[0] ?? '';
    return `${content.slice(0, match.index)}${RENDER_SIGNAL_SCRIPT_TAG}\n${indent}${content.slice(match.index)}`;
  }

  const bodyClose = /<\/body>/i;
  if (bodyClose.test(content)) {
    return content.replace(bodyClose, (m) => `  ${RENDER_SIGNAL_SCRIPT_TAG}\n  ${m}`);
  }
  return content;
}

async function readWorkspaceFile(
  wc: WebContainer | null,
  path: string,
  templateFiles: Record<string, string>
): Promise<string | undefined> {
  const fromStore = workbenchStore.files.get()[path]?.content;
  if (fromStore !== undefined) return fromStore;
  if (!wc) return templateFiles[path];
  try {
    return await wc.fs.readFile(path, 'utf-8');
  } catch {
    return templateFiles[path];
  }
}

/**
 * With no WebContainer (the light preview) the files are written to the file store only, which
 * the light preview compiles from: a restored snapshot's older bridge is upgraded and `main.tsx`
 * gets `initPreviewBridge()` the same way.
 *
 * Returns true when something was written, which means the preview is about to compile again
 * and reload. The caller waits for that rather than handing the founder a page that is on its
 * way out - their first click used to land on it and be lost.
 */
export async function ensureHotEditBridgeFiles(wc: WebContainer | null): Promise<boolean> {
  let wrote = false;
  const templateFiles = getHeadlessTemplateFiles();

  for (const [relPath, body] of Object.entries(templateFiles)) {
    if (!relPath.startsWith(IDEoz_BRIDGE_PREFIX)) continue;
    let existing: string | undefined = workbenchStore.files.get()[relPath]?.content;
    if (existing === undefined && wc) {
      try {
        existing = await wc.fs.readFile(relPath, 'utf-8');
      } catch {
        /* file missing — will write template */
      }
    }
    if (existing === body) continue;
    await persistUserFile(wc, relPath, body, false);
    wrote = true;
  }

  const indexPath = 'index.html';
  const indexContent = await readWorkspaceFile(wc, indexPath, templateFiles);
  if (indexContent) {
    const nextIndex = patchIndexHtmlForRenderSignal(indexContent);
    if (nextIndex !== indexContent) {
      await persistUserFile(wc, indexPath, nextIndex, false);
      wrote = true;
    }
  }

  const mainPath = 'src/main.tsx';
  const mainContent = await readWorkspaceFile(wc, mainPath, templateFiles);
  if (!mainContent) {
    materializeHotEditBridgeFilesInStore();
    return wrote;
  }

  const nextMain = patchMainTsxForBridge(mainContent);
  if (nextMain !== mainContent) {
    await persistUserFile(wc, mainPath, nextMain, false);
    wrote = true;
  }

  materializeHotEditBridgeFilesInStore();
  return wrote;
}
